import { api } from "services/api";
import type { PaginatedResponse } from "types/domain";
import type {
  AttendanceDetail,
  AttendanceItem,
  BulkManualAttendancePayload,
  BulkManualAttendanceResponse,
  ClientGeoLocation,
  FaceAttendanceAction,
  FaceAttendanceResult,
  FaceImageInput,
  ManualAttendancePayload,
} from "./types";

function dataUrlToBlob(dataUrl: string): Blob | null {
  const match = dataUrl.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,(.+)$/);
  if (!match) return null;

  const mimeType = match[1];
  const base64Data = match[2];

  if (typeof atob !== "function") return null;

  try {
    const binary = atob(base64Data);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return new Blob([bytes], { type: mimeType });
  } catch {
    return null;
  }
}

function appendImage(formData: FormData, image: FaceImageInput) {
  if (typeof image === "string") {
    const normalized = image.trim();
    const blob = dataUrlToBlob(normalized);
    if (blob) {
      formData.append("file", blob, "capture.jpg");
      return;
    }

    const base64 = normalized.includes(",")
      ? normalized.split(",")[1]
      : normalized;
    formData.append("image_base64", base64);
    return;
  }

  formData.append("file", image, "capture.jpg");
}

function normalizeAttendanceResult(
  data: FaceAttendanceResult & {
    user_name?: string | null;
    check_in_score?: number | null;
  }
): FaceAttendanceResult {
  return {
    face_check_id: data.face_check_id ?? null,
    matched: Boolean(data.matched),
    full_name: data.full_name ?? data.user_name ?? null,
    user_id: data.user_id ?? null,
    attendance_id: data.attendance_id ?? null,
    similarity_score: data.similarity_score ?? data.check_in_score ?? null,
    action: data.action ?? null,
    // Services can't translate; callers render this through t().
    message: data.message ?? "teaching.actionSuccess",
    requires_session_enrollment: Boolean(data.requires_session_enrollment),
  };
}

export const teachingAttendanceService = {
  faceCheckAttendance: async (
    sessionId: string,
    image: FaceImageInput,
    location?: ClientGeoLocation,
    action?: FaceAttendanceAction,
    classId?: string
  ): Promise<FaceAttendanceResult> => {
    const form = new FormData();
    appendImage(form, image);
    if (classId) {
      form.append("class_id", classId);
    }
    if (sessionId) {
      form.append("session_id", sessionId);
    }
    if (action) {
      form.append("action", action);
    }
    if (location) {
      form.append(
        "location",
        `${location.latitude.toFixed(6)},${location.longitude.toFixed(6)}`
      );
    }

    const response = await api.upload<
      FaceAttendanceResult & {
        user_name?: string | null;
        check_in_score?: number | null;
      }
    >("/attendance/face-check", form);

    return normalizeAttendanceResult(response);
  },

  getSessionAttendance: async (
    sessionId: string
  ): Promise<AttendanceItem[]> => {
    const response = await api.get<PaginatedResponse<AttendanceItem>>(
      `/attendance?session_id=${sessionId}&skip=0&limit=200`
    );
    return response.items;
  },

  submitManualAttendance: async (
    payload: ManualAttendancePayload
  ): Promise<AttendanceItem> => {
    return api.post<AttendanceItem>("/attendance/check", payload);
  },

  getAttendanceById: async (
    attendanceId: string
  ): Promise<AttendanceDetail> => {
    return api.get<AttendanceDetail>(`/attendance/${attendanceId}`);
  },

  bulkManualAttendance: async (
    items: BulkManualAttendancePayload[],
    notes?: string
  ): Promise<BulkManualAttendanceResponse> => {
    return api.post<BulkManualAttendanceResponse>(
      "/attendance/bulk-manual-check",
      {
        items,
        notes,
      }
    );
  },

  updateEnrollmentPhoto: async (
    classId: string,
    enrollmentId: string,
    photoUrl: string
  ): Promise<void> => {
    await api.patch(`/classes/${classId}/enrollments/${enrollmentId}/photo`, {
      photo_url: photoUrl,
    });
  },
};
